import { useEffect, useId, useRef, useState } from 'react';
import { apiCall } from '../api/client';
import Markdown from './Markdown';
import OpenRouterModelPicker from './OpenRouterModelPicker';

// A separate conversation with no lesson setters or save callbacks. Keying the
// session prevents history or late replies leaking into another lesson/course.
export default function LessonPlannerChat(props) {
  return <PlanningSession key={`${props.courseId}:${props.lessonId || 'new'}`} {...props} />;
}

function PlanningSession({ courseId, lessonId, token, draft, precedingLevelCount, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [model, setModel] = useState('');
  const active = useRef(true);
  const sending = useRef(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const id = useId();

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight });
  }, [messages, pending, open]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  async function send() {
    const message = input.trim();
    if (!message || sending.current || disabled) return;
    sending.current = true;
    setPending(message);
    setError('');
    // Bound recent history while keeping complete user/assistant pairs.
    const history = messages.slice(-12).map(turn => ({ ...turn, content: turn.content.slice(0, 8000) }));
    while (JSON.stringify(history).length > 24000) history.splice(0, 2);
    try {
      const result = await apiCall('POST', `/courses/${courseId}/lesson-planner`, {
        ...(lessonId ? { lessonId } : {}), ...(model ? { model } : {}), draft, message, history,
      }, token);
      if (!result.reply?.trim()) throw new Error('The planning assistant could not respond. Please try again.');
      if (active.current) {
        setMessages(prev => [...prev, { role: 'user', content: message }, { role: 'assistant', content: result.reply }]);
        setContext(result.context);
        setInput('');
      }
    } catch (err) {
      if (active.current) setError(err.message || 'Could not reach the planning assistant. Please try again.');
    } finally {
      sending.current = false;
      if (active.current) setPending('');
    }
  }

  const count = precedingLevelCount ?? context?.precedingLevelCount;
  return <section className="border border-amber-200 rounded-xl overflow-hidden">
    <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}
      className="w-full text-left px-4 py-3 bg-amber-50 text-sm font-semibold text-amber-800 hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-400">
      {open ? 'Hide planning chat' : 'Plan with AI'}
    </button>
    <div id={id} hidden={!open} className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-amber-800">Grammar lesson planning</h4>
          <p className="text-xs text-amber-600 mt-1">
            {typeof count === 'number'
              ? `Planning level ${count + 1}, after ${count} earlier ${count === 1 ? 'level' : 'levels'}. `
              : 'Uses the levels before this lesson. '}
            Includes saved earlier vocabulary and lessons, plus your current draft. Suggestions only; the assistant cannot change your lesson.
          </p>
          <p className="text-xs text-amber-500 mt-1">Chat stays here while you edit and clears when you leave this page. Replies use the recent conversation.</p>
        </div>
        <button type="button" disabled={!!pending || !messages.length} onClick={() => { setMessages([]); setError(''); setContext(null); }}
          className="shrink-0 text-xs text-amber-700 underline disabled:opacity-40">Clear chat</button>
      </div>
      {open && <OpenRouterModelPicker courseId={courseId} token={token} value={model} onChange={setModel} disabled={!!pending} />}
      {context?.contextTruncated && <p className="text-xs text-amber-700">This course is large, so some earlier content was omitted from the assistant's context.</p>}
      <div role="log" aria-label="Lesson planning conversation" aria-live="polite" aria-busy={!!pending}
        ref={scrollRef} className="max-h-96 overflow-y-auto space-y-3 text-sm">
        {!messages.length && !pending && <p className="text-amber-600 py-2">Ask for an outline, suitable examples, or a check of the lesson's prerequisites.</p>}
        {[...messages, ...(pending ? [{ role: 'user', content: pending }] : [])].map((turn, index) => (
          <div key={index} className={`rounded-lg px-3 py-2 break-words ${turn.role === 'user' ? 'bg-amber-100 ml-6' : 'bg-amber-50 mr-6'}`}>
            <p className="text-xs font-semibold text-amber-600 mb-1">{turn.role === 'user' ? 'You' : 'Planning assistant'}</p>
            {turn.role === 'assistant' ? <Markdown>{turn.content}</Markdown> : <p className="whitespace-pre-wrap text-amber-900">{turn.content}</p>}
          </div>
        ))}
        {pending && <p role="status" className="text-amber-600">Thinking…</p>}
      </div>
      {error && <p role="alert" className="text-sm text-red-600">{error} Your message is still below; send it again to retry.</p>}
      {disabled && <p className="text-xs text-amber-600">Load the lesson content before chatting so the assistant can see your draft.</p>}
      <label htmlFor={`${id}-message`} className="block text-sm font-medium text-amber-700">Message the planning assistant</label>
      <textarea id={`${id}-message`} ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
        }}
        rows={3} maxLength={4000} disabled={!!pending || disabled}
        placeholder="What would be a good way to introduce this grammar at this point in the course?"
        className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-gray-50 resize-y" />
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-amber-500">Enter to send · Shift+Enter for a new line</span>
        <button type="button" onClick={send} disabled={!!pending || disabled || !input.trim()}
          className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg">
          {pending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  </section>;
}
